import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Loader2,
  Sparkles,
  Check,
  RotateCcw,
  X,
  AlignLeft,
  AlignJustify,
  User,
  Bot,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../config';
import { cn } from '../../lib/utils';

/** @typedef {'write' | 'summary' | 'grammar' | 'shorten'} AiModalMode */

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'technical', label: 'Technical' },
];

function buildWriteRequestText(conversation, tone, documentPlain, documentTitle) {
  const toneLine = `Writing tone: ${tone}.`;
  const titleLine = documentTitle ? `Document title: ${documentTitle}.\n\n` : '';
  const docSection =
    documentPlain && documentPlain.trim()
      ? `${titleLine}Current document (plain text):\n${documentPlain.trim().slice(0, 100_000)}\n\n---\n\n`
      : titleLine
        ? `${titleLine}---\n\n`
        : '';
  const conv =
    conversation.length === 0
      ? ''
      : `Conversation:\n${conversation
          .map((m) => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`)
          .join('\n\n')}\n\n`;
  return `${toneLine}\n\n${docSection}${conv}Respond as the Assistant to the latest User message only. Output markdown when appropriate.`;
}

async function postDocAssistant(organizationId, task, text, contextTitle) {
  const { data } = await axios.post(`${API_URL}/organizations/${organizationId}/ai/doc-assistant`, {
    task,
    text,
    context_title: contextTitle || undefined,
  });
  return data;
}

/**
 * Archbee-style document AI: chat for "Write with AI", quick actions for summary / grammar / shorten.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {AiModalMode} props.mode
 * @param {string} [props.organizationId]
 * @param {string} [props.documentTitle]
 * @param {() => Promise<string>} props.getFullDocumentText
 * @param {() => string} props.getSelectionText
 * @param {(plainOutput: string) => Promise<void>} props.onInsertAtEnd
 * @param {(plainOutput: string) => Promise<void>} props.onInsertSummaryCallout
 */
export default function EditorAiAssistantModal({
  isOpen,
  onClose,
  mode = 'write',
  organizationId,
  documentTitle,
  getFullDocumentText,
  getSelectionText,
  onInsertAtEnd,
  onInsertSummaryCallout,
}) {
  const [addonEnabled, setAddonEnabled] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  /** @type {{ id: string, role: 'user' | 'assistant', content: string }[]} */
  const [conversation, setConversation] = useState([]);
  const [draft, setDraft] = useState('');
  const [tone, setTone] = useState('professional');
  const [running, setRunning] = useState(false);
  const [meta, setMeta] = useState(null);
  /** Quick modes: source body */
  const [quickBody, setQuickBody] = useState('');
  const [quickResult, setQuickResult] = useState('');
  const scrollRef = useRef(null);

  const loadSubscription = useCallback(async () => {
    if (!organizationId) {
      setAddonEnabled(false);
      return;
    }
    setSubscriptionLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/organizations/${organizationId}/subscription`);
      setAddonEnabled(!!data?.ai_addon?.enabled);
    } catch (e) {
      console.error(e);
      setAddonEnabled(false);
      toast.error('Could not load subscription');
    } finally {
      setSubscriptionLoading(false);
    }
  }, [organizationId]);

  const resetState = useCallback(() => {
    setConversation([]);
    setDraft('');
    setTone('professional');
    setMeta(null);
    setQuickBody('');
    setQuickResult('');
    setRunning(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    resetState();
    loadSubscription();
  }, [isOpen, loadSubscription, mode, resetState]);

  const loadQuickBody = useCallback(async () => {
    try {
      const sel = getSelectionText?.()?.trim();
      if (sel) {
        setQuickBody(sel);
        return;
      }
      const full = await getFullDocumentText?.();
      setQuickBody((full || '').trim());
    } catch (e) {
      console.error(e);
      toast.error('Could not read editor content');
      setQuickBody('');
    }
  }, [getSelectionText, getFullDocumentText]);

  useEffect(() => {
    if (!isOpen || addonEnabled !== true) return;
    if (mode !== 'write') {
      loadQuickBody();
    }
  }, [isOpen, addonEnabled, mode, loadQuickBody]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [conversation, quickResult]);

  const modeTitle =
    mode === 'write'
      ? 'AI Write'
      : mode === 'summary'
        ? 'Generate summary'
        : mode === 'grammar'
          ? 'Fix grammar and spelling'
          : 'Make it shorter';

  const runQuick = async (task) => {
    if (!organizationId) {
      toast.error('No workspace');
      return;
    }
    const text = quickBody.trim();
    if (!text) {
      toast.error('No text in the document (or selection). Add content or select text first.');
      return;
    }
    setRunning(true);
    setQuickResult('');
    setMeta(null);
    try {
      const data = await postDocAssistant(organizationId, task, text, documentTitle);
      setQuickResult(data.output || '');
      setMeta({
        provider: data.provider,
        model: data.model,
        total_tokens: data.total_tokens,
      });
    } catch (e) {
      handleAiError(e);
    } finally {
      setRunning(false);
    }
  };

  const handleAiError = (e) => {
    const status = e.response?.status;
    const detail = e.response?.data?.detail;
    if (status === 429) {
      toast.error('Monthly AI token quota exceeded for this workspace.');
    } else {
      toast.error(typeof detail === 'string' ? detail : 'AI request failed');
    }
  };

  const runWriteGenerate = async () => {
    if (!organizationId) {
      toast.error('No workspace');
      return;
    }
    const userMsg = draft.trim();
    if (!userMsg) {
      toast.error('Describe what you want to write…');
      return;
    }
    setRunning(true);
    setMeta(null);
    const userTurn = { id: crypto.randomUUID(), role: 'user', content: userMsg };
    const nextConv = [...conversation, userTurn];
    setConversation(nextConv);
    setDraft('');
    try {
      const docPlain = await getFullDocumentText?.();
      const payload = buildWriteRequestText(nextConv, tone, docPlain || '', documentTitle);
      const data = await postDocAssistant(organizationId, 'write', payload, documentTitle);
      setConversation((c) => [
        ...c,
        { id: crypto.randomUUID(), role: 'assistant', content: data.output || '' },
      ]);
      setMeta({
        provider: data.provider,
        model: data.model,
        total_tokens: data.total_tokens,
      });
    } catch (e) {
      setConversation((c) => c.filter((m) => m.id !== userTurn.id));
      setDraft(userMsg);
      handleAiError(e);
    } finally {
      setRunning(false);
    }
  };

  const tryAgainWrite = async () => {
    let msgs = [...conversation];
    if (msgs.length && msgs[msgs.length - 1].role === 'assistant') {
      msgs = msgs.slice(0, -1);
      setConversation(msgs);
    }
    const lastUser = [...msgs].reverse().find((m) => m.role === 'user');
    if (!lastUser) return;
    setRunning(true);
    setMeta(null);
    try {
      const docPlain = await getFullDocumentText?.();
      const payload = buildWriteRequestText(msgs, tone, docPlain || '', documentTitle);
      const data = await postDocAssistant(organizationId, 'write', payload, documentTitle);
      setConversation((c) => [...c, { id: crypto.randomUUID(), role: 'assistant', content: data.output || '' }]);
      setMeta({
        provider: data.provider,
        model: data.model,
        total_tokens: data.total_tokens,
      });
    } catch (e) {
      handleAiError(e);
    } finally {
      setRunning(false);
    }
  };

  const tryAgainQuick = async () => {
    const task = mode === 'summary' ? 'summarize' : mode === 'grammar' ? 'grammar' : 'shorten';
    await runQuick(task);
  };

  const dismissLastAssistant = () => {
    setConversation((c) => (c.length && c[c.length - 1].role === 'assistant' ? c.slice(0, -1) : c));
    setQuickResult('');
  };

  const lastAssistantContent = () => {
    if (quickResult) return quickResult;
    for (let i = conversation.length - 1; i >= 0; i--) {
      if (conversation[i].role === 'assistant') return conversation[i].content;
    }
    return '';
  };

  const acceptInsert = async () => {
    const out = lastAssistantContent().trim();
    if (!out) return;
    try {
      await onInsertAtEnd(out);
      onClose();
    } catch (e) {
      console.error(e);
      toast.error('Could not insert into document');
    }
  };

  const acceptSummaryBlock = async () => {
    const out = quickResult.trim();
    if (!out) return;
    try {
      await onInsertSummaryCallout?.(out);
      onClose();
    } catch (e) {
      console.error(e);
      toast.error('Could not insert summary block');
    }
  };

  const runAdjustOnLastOutput = async (task) => {
    let base = '';
    if (mode === 'write') {
      if (!conversation.length || conversation[conversation.length - 1].role !== 'assistant') {
        toast.message('Generate content first');
        return;
      }
      base = conversation[conversation.length - 1].content.trim();
    } else {
      base = lastAssistantContent().trim();
    }
    if (!base) {
      toast.message('Generate content first');
      return;
    }
    if (!organizationId) return;
    setRunning(true);
    try {
      const data = await postDocAssistant(organizationId, task, base, documentTitle);
      const next = data.output || '';
      if (mode === 'write') {
        setConversation((c) => {
          const copy = [...c];
          if (copy.length && copy[copy.length - 1].role === 'assistant') {
            copy.pop();
          }
          return [...copy, { id: crypto.randomUUID(), role: 'assistant', content: next }];
        });
      } else {
        setQuickResult(next);
      }
      setMeta({
        provider: data.provider,
        model: data.model,
        total_tokens: data.total_tokens,
      });
    } catch (e) {
      handleAiError(e);
    } finally {
      setRunning(false);
    }
  };

  const refreshQuickSource = () => {
    loadQuickBody();
    toast.success('Refreshed from editor');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={cn(
          'max-h-[min(90vh,720px)] flex flex-col gap-0 p-0 overflow-hidden',
          mode === 'write' ? 'max-w-lg' : 'max-w-lg'
        )}
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-200 dark:border-slate-800 shrink-0">
          <div className="flex items-start justify-between gap-2">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold pr-8">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-white">
                <Sparkles className="h-4 w-4" />
              </span>
              {modeTitle}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col px-5 pb-5 pt-3">
          {subscriptionLoading && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
              <Loader2 className="w-4 h-4 animate-spin" />
              Checking workspace…
            </div>
          )}

          {!organizationId && !subscriptionLoading && (
            <p className="text-sm text-slate-500 py-4">Select a workspace with a document open.</p>
          )}

          {addonEnabled === true && mode === 'write' && (
            <>
              <div
                ref={scrollRef}
                className="flex-1 min-h-[200px] max-h-[340px] overflow-y-auto pr-2 space-y-4 pb-2"
              >
                {conversation.length === 0 && (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Describe what you want to write. The current document is sent as context when you generate.
                  </p>
                )}
                {conversation.map((m) => (
                  <div
                    key={m.id}
                    className={cn('flex gap-3', m.role === 'user' ? 'flex-row-reverse' : 'flex-row')}
                  >
                    <div
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                        m.role === 'user' ? 'bg-slate-200 dark:bg-slate-700' : 'bg-blue-600 text-white'
                      )}
                    >
                      {m.role === 'user' ? (
                        <User className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                      ) : (
                        <Bot className="h-4 w-4" />
                      )}
                    </div>
                    <div
                      className={cn(
                        'rounded-xl px-3 py-2 text-sm max-w-[85%] whitespace-pre-wrap',
                        m.role === 'user'
                          ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100'
                          : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200'
                      )}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {running && (
                  <div className="flex gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                    <span className="text-sm text-slate-500">Generating…</span>
                  </div>
                )}
              </div>

              {conversation.length > 0 && conversation[conversation.length - 1].role === 'assistant' && !running && (
                <div className="flex flex-wrap gap-2 py-3 border-t border-slate-100 dark:border-slate-800 mt-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-800"
                    onClick={acceptInsert}
                    data-testid="ai-accept-insert"
                  >
                    <Check className="h-3.5 w-3.5 mr-1.5" />
                    Accept & insert
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={tryAgainWrite} data-testid="ai-try-again">
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                    Try again
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="text-slate-500" onClick={dismissLastAssistant}>
                    <X className="h-3.5 w-3.5 mr-1.5" />
                    Dismiss
                  </Button>
                </div>
              )}

              <div className="pt-2 space-y-2">
                <Textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Please describe in details about what you want to write…"
                  rows={3}
                  className="resize-none text-sm"
                  data-testid="ai-write-draft"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      runWriteGenerate();
                    }
                  }}
                />
                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    onClick={runWriteGenerate}
                    disabled={running || !draft.trim()}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="ai-generate-write"
                  >
                    {running ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-1.5" />
                        Generate
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-3 mt-2 border-t border-slate-200 dark:border-slate-800">
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="ai-tone-select">
                    <SelectValue placeholder="Tone" />
                  </SelectTrigger>
                  <SelectContent>
                    {TONE_OPTIONS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        Tone · {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  disabled={running}
                  onClick={() => runAdjustOnLastOutput('shorten')}
                >
                  <AlignLeft className="h-3.5 w-3.5 mr-1" />
                  Make it shorter
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  disabled={running}
                  onClick={() => runAdjustOnLastOutput('lengthen')}
                >
                  <AlignJustify className="h-3.5 w-3.5 mr-1" />
                  Make it longer
                </Button>
              </div>

              {meta && (
                <p className="text-[10px] text-slate-400 pt-2">
                  {meta.provider} · {meta.model} · ~{meta.total_tokens} tokens
                </p>
              )}
            </>
          )}

          {addonEnabled === true && mode !== 'write' && (
            <>
              <div className="space-y-2 mb-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-slate-500 uppercase tracking-wide">Source</Label>
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={refreshQuickSource}>
                    Refresh from editor
                  </Button>
                </div>
                <div className="h-[120px] overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 p-3">
                  <p className="text-xs text-slate-600 dark:text-slate-400 whitespace-pre-wrap font-mono">
                    {quickBody ? quickBody.slice(0, 8000) : 'No text — add content or select text in the document.'}
                    {quickBody.length > 8000 ? '…' : ''}
                  </p>
                </div>
              </div>

              <Button
                type="button"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white mb-3"
                disabled={running || !quickBody.trim()}
                onClick={() => runQuick(mode === 'summary' ? 'summarize' : mode === 'grammar' ? 'grammar' : 'shorten')}
                data-testid="ai-quick-generate"
              >
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Generate'}
              </Button>

              {quickResult && (
                <>
                  <div className="rounded-lg border-l-4 border-blue-600 bg-blue-50/80 dark:bg-blue-950/30 border border-slate-200 dark:border-slate-700 p-3 mb-3">
                    <p className="text-xs font-semibold text-blue-900 dark:text-blue-200 mb-2">
                      {mode === 'summary' ? 'Summary' : mode === 'grammar' ? 'Corrected' : 'Shorter version'}
                    </p>
                    <div className="max-h-[200px] overflow-y-auto">
                      <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{quickResult}</p>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">— AI Generated</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {mode === 'summary' && (
                      <Button
                        type="button"
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={acceptSummaryBlock}
                        data-testid="ai-insert-summary-callout"
                      >
                        <Check className="h-3.5 w-3.5 mr-1.5" />
                        Insert summary block
                      </Button>
                    )}
                    <Button type="button" size="sm" variant="outline" onClick={acceptInsert}>
                      <Check className="h-3.5 w-3.5 mr-1.5" />
                      Accept & insert
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={tryAgainQuick}>
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                      Try again
                    </Button>
                    <Button type="button" size="sm" variant="ghost" className="text-slate-500" onClick={dismissLastAssistant}>
                      <X className="h-3.5 w-3.5 mr-1.5" />
                      Dismiss
                    </Button>
                  </div>
                </>
              )}

              {meta && quickResult && (
                <p className="text-[10px] text-slate-400 pt-2">
                  {meta.provider} · {meta.model} · ~{meta.total_tokens} tokens
                </p>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
